import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  IconButton,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { FileDownload, Print } from "@mui/icons-material";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
// import DynamicTable from "../../../../components/Dynamic-table";
import DynamicTable from "../../../../components/lab-dynamic-table";
import {
  getDefaultRecordDeleteTooltip,
  getDefaultRecordEditTooltip,
  isDefaultLabRecord,
} from "../../utils/defaultRecordActions";

import DeleteConfirmationModal from "../../../../components/Modals/delete-modal";

export enum MEASURING_TYPES {
  Mass = "Mass",
  Liquid = "Liquid",
  Gas = "Gas",
  Count = "Count",
}

export enum THRESHOLD_MEASURING_TYPES {
  Milliliter = "Milliliter",
  Microliter = "Microliter",
  Liter = "Liter",
  Drop = "Drop",
}

const Assets: React.FC<any> = () => {
  const [editData, setEditData] = useState<any>(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState<any>("");
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterCategoryId, setFilterCategoryId] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState("");

  const [formData, setFormData] = useState({
    assetName: "",
    description: "",
    measuringType: "Mass",
    categoryId: "",
    alertThreshold: "",
    threshold_measuring_type: "Milliliter",
    expiryDays: "",
  });

  const [errors, setErrors] = useState({
    assetName: false,
    measuringType: false,
    categoryId: false,
  });

  const [assetsData, setAssetsData] = useState<any>();

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchInput), 500);
    return () => clearTimeout(handler);
  }, [searchInput]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/assetCategory/getAllBySearch`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (res?.data?.isSuccess) {
        setCategories(res.data?.data || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAssets = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/assets/getAllAssets`,
        {
          params: {
            count: rowsPerPage,
            pageNo: page + 1,
            search: debouncedSearch,
            ...(filterCategoryId && { categoryId: filterCategoryId }),
          },
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (res?.data.isSuccess) {
        setAssetsData(res.data?.data);
      }
      setTotal(res.data?.totalCount);
    } catch (error) {
      console.error(error);
      toast.error("Error fetching assets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchAssets();
  }, [page, rowsPerPage, debouncedSearch, filterCategoryId]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "alertThreshold" || name === "expiryDays" ? value.replace(/^0+/, "") : value,
    }));
    // Clear error when user types
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [name]: false }));
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    const newErrors = {
      assetName: !formData.assetName,
      measuringType: !formData.measuringType,
      categoryId: !formData.categoryId,
    };
    
    setErrors(newErrors);
    
    // Check if there are any errors
    if (Object.values(newErrors).some((error) => error)) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    const payload: any = {
      assetName: formData.assetName,
      description: formData.description,
      measuringType: formData.measuringType,
      categoryId: formData.categoryId,
      alertThreshold: formData.alertThreshold === "" ? 0 : Number(formData.alertThreshold),
      expiryDays: formData.expiryDays === "" ? 0 : Number(formData.expiryDays),
    };
    
    // Only include threshold_measuring_type if it has a valid value
    if (formData.threshold_measuring_type && formData.threshold_measuring_type !== "") {
      payload.threshold_measuring_type = formData.threshold_measuring_type;
    }
    try {
      if (editData) {
        await axios.put(
          `${process.env.REACT_APP_LAB_URL}/assets/updateById/${editData}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        toast.success("Asset updated successfully");
      } else {
        await axios.post(
          `${process.env.REACT_APP_LAB_URL}/assets/addAssets`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        toast.success("Asset added successfully");
      }
      fetchAssets();
      setEditData(null);
      setFormData({
        assetName: "",
        description: "",
        measuringType: "Mass",
        categoryId: "",
        alertThreshold: "",
        threshold_measuring_type: "Milliliter",
        expiryDays: "",
      });
      setErrors({
        assetName: false,
        measuringType: false,
        categoryId: false,
      });
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleEditClick = (item: any) => {
    setEditData(item._id);
    setFormData({
      assetName: item.assetName,
      description: item.description || "",
      measuringType: item.measuringType,
      categoryId: item.categoryId._id || "",
      alertThreshold: item.alertThreshold || "",
      threshold_measuring_type: item.threshold_measuring_type || "Milliliter",
      expiryDays: item.expiryDays || "",
    });
    setErrors({
      assetName: false,
      measuringType: false,
      categoryId: false,
    });
  };

  const handleDeleteClick = (id: string) => {
    setIsDeleteModalOpen(true);
    setSelectedItemId(id);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_LAB_URL}/assets/deleteById/${selectedItemId}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      if (response?.data.isSuccess) {
        toast.success(response?.data.message);
      }
      fetchAssets();
      setEditData(null);
      setFormData({
        assetName: "",
        description: "",
        measuringType: "Mass",
        categoryId: "",
        alertThreshold: "",
        threshold_measuring_type: "Milliliter",
        expiryDays: "",
      });
      setErrors({
        assetName: false,
        measuringType: false,
        categoryId: false,
      });
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsDeleteModalOpen(false);
    }
  };

  // Export to PDF function
  const exportToPDF = () => {
    if (!assetsData?.length) return toast.warn("No data to export.");

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 8;
    let yPosition = margin;

    // Header
    doc
      .setFontSize(14)
      .setFont("helvetica", "bold")
      .setTextColor(25, 55, 85)
      .text("ASSETS REPORT", pageWidth / 2, yPosition, {
        align: "center",
      });

    yPosition += 6;
    doc
      .setDrawColor(25, 55, 85)
      .setLineWidth(0.5)
      .line(margin, yPosition, pageWidth - margin, yPosition);

    // Metadata
    yPosition += 5;
    doc
      .setFontSize(8)
      .setFont("helvetica", "normal")
      .setTextColor(80, 80, 80)
      .text(
        `Category: ${
          categories.find((cat) => cat._id === filterCategoryId)?.name || "All"
        } | Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
        pageWidth / 2,
        yPosition,
        { align: "center" }
      );

    yPosition += 8;

    const head = [
      ["SN", "Asset Name", "Alert Threshold", "Threshold Type", "Measuring Type", "Expiry Days", "Category"]
    ];

    const body = assetsData.map((item: any, index: number) => [
      index + 1,
      item.assetName || "",
      item.alertThreshold || "",
      item.threshold_measuring_type || "",
      item.measuringType || "",
      item.expiryDays || "",
      categories.find((cat) => cat._id === item.categoryId._id)?.name || "",
    ]);

    autoTable(doc, {
      startY: yPosition,
      head,
      body,
      headStyles: {
        fillColor: [25, 55, 85],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8,
        // halign: 'center',
        // valign: 'middle',
        cellPadding: 3
      },
      bodyStyles: {
        fontSize: 7,
        textColor: [40, 40, 40],
        cellPadding: 2,
        valign: 'middle'
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250]
      },
      margin: { top: margin, right: margin, bottom: margin, left: margin },
      didDrawPage: function(data) {
        const pageSize = doc.internal.pageSize;
        const pageCount = doc.getNumberOfPages();
        const pageNum = data.pageNumber;

        doc.setFontSize(6);
        doc.setTextColor(120, 120, 120);
        doc.text(
          `Page ${pageNum} of ${pageCount}`,
          pageSize.getWidth() / 2,
          pageSize.getHeight() - 4,
          { align: "center" }
        );
      }
    });

    // doc.autoPrint();
    doc.output("dataurlnewwindow");
  };

  // Export to Excel function
  const exportToExcel = () => {
    if (!assetsData?.length) return toast.warn("No data to export.");

    const worksheetData = assetsData.map((item: any, index: number) => ({
      SN: index + 1,
      "Asset Name": item.assetName || "",
      "Alert Threshold": item.alertThreshold || "",
      "Threshold Type": item.threshold_measuring_type || "",
      "Measuring Type": item.measuringType || "",
      "Expiry Days": item.expiryDays || "",
      Category: categories.find((cat) => cat._id === item.categoryId._id)?.name || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Assets");
    XLSX.writeFile(workbook, "Assets.xlsx");
  };

  const onMeasuringTypeChange = (_: any, value: string | null) =>
    handleChange({
      target: { name: "measuringType", value: value ?? "" },
    } as any);

  return (
    <Box>
      <Box
        sx={{
          borderRadius: 2,
          padding: "1px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
          }}
        >
          Assets
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<FileDownload />}
            onClick={exportToExcel}
            sx={{
              minWidth: { xs: "auto", md: "80px" },
              fontSize: { xs: "12px", md: "12px" },
              fontWeight: "600",
              textTransform: "none",
              borderRadius: "8px",
              background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
              color: "#ffffff",
              boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
              transition: "all 0.3s ease-in-out",
              "&:hover": {
                background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
                boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
                transform: "translateY(-1px)",
              },
            }}
          >
            Excel
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Print />}
            onClick={exportToPDF}
            sx={{
              minWidth: { xs: "auto", md: "80px" },
              fontSize: { xs: "12px", md: "12px" },
              fontWeight: "600",
              textTransform: "none",
              borderRadius: "8px",
              background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
              color: "#ffffff",
              boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
              transition: "all 0.3s ease-in-out",
              "&:hover": {
                background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
                boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
                transform: "translateY(-1px)",
              },
            }}
          >
            Print
          </Button>
          <Autocomplete
            size="small"
            options={categories}
            getOptionLabel={(option) => option.name || ""}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            value={
              categories.find((cat) => cat._id === filterCategoryId) ?? null
            }
            onChange={(_, newValue) => {
              setFilterCategoryId(newValue?._id ?? "");
              setPage(0);
            }}
            sx={{ minWidth: { xs: 160, md: 200 } }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Category"
                className="custom-input"
                placeholder="All Categories"
              />
            )}
          />
          <TextField
            label="Search"
            placeholder="Search assets..."
            variant="outlined"
            className="custom-input"
            size="small"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              endAdornment: (
                <IconButton>
                  <SearchIcon sx={{ color: "#055FFC" }} />
                </IconButton>
              ),
            }}
          />
        </Box>
      </Box>

      <Grid container spacing={1}>
        <Grid item xs={12} sm={4}>
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 2,
              px: 3,
              py: 1,
              boxShadow: 1,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              {editData ? "Update Asset" : "Add New Asset"}
            </Typography>

            <TextField
              label="Asset Name"
              name="assetName"
              value={formData.assetName}
              fullWidth
              size="small"
              onChange={handleChange}
              className="custom-input"
              required
              error={errors.assetName}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Description"
              name="description"
              value={formData.description}
              fullWidth
              size="small"
              multiline
              rows={2}
              onChange={handleChange}
              className="custom-input"
              sx={{ mb: 2 }}
            />
            
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Autocomplete
                size="small"
                options={Object.values(MEASURING_TYPES)}
                value={formData.measuringType ?? null}
                onChange={(_, value) => {
                  onMeasuringTypeChange(_, value);
                  if (errors.measuringType) {
                    setErrors((prev) => ({ ...prev, measuringType: false }));
                  }
                }}
                sx={{ flex: 1 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Measuring unit"
                    className="custom-input"
                    placeholder="Select or type..."
                    required
                    error={errors.measuringType}
                  />
                )}
              />
              <Autocomplete
                size="small"
                options={Object.values(THRESHOLD_MEASURING_TYPES)}
                value={formData.threshold_measuring_type ?? null}
                onChange={(_, value) =>
                  handleChange({
                    target: { name: "threshold_measuring_type", value: value ?? "" },
                  } as any)
                }
                sx={{ flex: 1 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Threshold"
                    className="custom-input"
                    placeholder="Select threshold type..."
                  />
                )}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                label="Expiry Days"
                name="expiryDays"
                className="custom-input"
                type="number"
                value={formData.expiryDays}
                size="small"
                onChange={handleChange}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Alert Threshold"
                name="alertThreshold"
                className="custom-input"
                type="number"
                value={formData.alertThreshold}
                size="small"
                onChange={handleChange}
                sx={{ flex: 1 }}
              />
            </Box>

            <Autocomplete
              size="small"
              options={categories}
              getOptionLabel={(option) => option.name} // what shows in the dropdown
              value={
                categories.find((cat) => cat._id === formData.categoryId) ??
                null
              }
              onChange={(_, newValue) => {
                handleChange({
                  target: {
                    name: "categoryId",
                    value: newValue?._id ?? "",
                  },
                } as any);
                if (errors.categoryId) {
                  setErrors((prev) => ({ ...prev, categoryId: false }));
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Category"
                  className="custom-input"
                  fullWidth
                  sx={{ mb: 2 }}
                  placeholder="Select or search..."
                  required
                  error={errors.categoryId}
                />
              )}
            />
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSubmit}
            >
              {editData ? "Update" : "Save"}
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} sm={8}>
          <DynamicTable
            headers={[
              "SN",
              "Asset Name",
              "Alert Threshold",
              "Threshold Type",
              "Measuring Type",
              "Expiry Days",
              "Category",
              "Actions",
            ]}
            data={assetsData?.map((item: any, index: number) => [
              page * rowsPerPage + index + 1,
              item.assetName || "",
              item.alertThreshold || "",
              item.threshold_measuring_type || "",
              item.measuringType || "",
              item.expiryDays || "",
              categories.find((cat) => cat._id === item.categoryId._id)?.name ||
                "",
            ])}
            loading={loading}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={(_e, newPage) => setPage(newPage)}
            handleChangeRowsPerPage={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            totalRows={total}
            actionButtons={[
              {
                icon: <EditIcon sx={{ color: "#FFB800", cursor: "pointer" }} />,
                onClick: (rowIndex: any) =>
                  handleEditClick(assetsData[rowIndex]),
                disabled: (rowIndex) => isDefaultLabRecord(assetsData[rowIndex]),
                tooltip: (rowIndex) =>
                  getDefaultRecordEditTooltip(assetsData[rowIndex]),
              },
              {
                icon: (
                  <DeleteIcon sx={{ color: "#FF5454", cursor: "pointer" }} />
                ),
                onClick: (rowIndex: any) =>
                  handleDeleteClick(assetsData[rowIndex]?._id),
                disabled: (rowIndex) => isDefaultLabRecord(assetsData[rowIndex]),
                tooltip: (rowIndex) =>
                  getDefaultRecordDeleteTooltip(assetsData[rowIndex]),
              },
            ]}
            height={"60vh"}
          />
          {assetsData?.length === 0 && (
            <h1
              style={{
                color: "gray",
                marginTop: "8px",
                textAlign: "center",
                fontSize: "12px",
                fontWeight: "bold",
                marginBottom: "8px",
              }}
            >
              No data found
            </h1>
          )}
        </Grid>
      </Grid>

      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        handleClose={() => setIsDeleteModalOpen(false)}
        handleConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};

export default Assets;
